import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  Loader2, Mail, Send, Workflow, Users, LayoutTemplate, Globe, Plus, RefreshCw, Trash2, Copy, Check,
  CheckCircle2, XCircle, Clock, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Modal, Badge, btnPrimary, btnGhost, inputCls } from '../components/ChatAssistant/ui';
import { mailApi } from '../components/GlowyMail/api';
import CampaignsTab from '../components/GlowyMail/Campaigns';
import ContactsTab from '../components/GlowyMail/Contacts';
import TemplatesTab from '../components/GlowyMail/Templates';

// Glowy Mail — e-posta pazarlama platformu. İskelet Glowy Desk ile aynı: üst şerit, solda menü, ortada panel.
// Kampanyalar / Kişiler / Şablonlar / Gönderim domain'leri çalışıyor; Akışlar henüz yok. Uçlar: /api/email-platform/* (sadece Efe).

const SEND_TABS = [
  { key: 'campaigns', label: 'Campaigns', icon: Send },
  { key: 'flows', label: 'Flows', icon: Workflow, soon: true },
  { key: 'contacts', label: 'Contacts', icon: Users },
  { key: 'templates', label: 'Templates', icon: LayoutTemplate },
];
const SETTINGS_TABS = [
  { key: 'domains', label: 'Sending domains', icon: Globe },
];

const STATUS = {
  verified: { tone: 'green', label: 'Verified' },
  pending: { tone: 'amber', label: 'Waiting for DNS' },
  failed: { tone: 'red', label: 'Verification failed' },
};

function CopyCell({ value }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1200); }
    catch (e) { toast.error('Could not copy'); }
  };
  return (
    <div className="flex items-start gap-1.5 min-w-0">
      <code className="text-[11px] text-gray-800 break-all leading-snug">{value}</code>
      <button type="button" onClick={copy} className="shrink-0 text-gray-400 hover:text-indigo-600" title="Copy">
        {done ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function RecordState({ rec, checked }) {
  if (!checked) return <span className="text-[11px] text-gray-400">not checked</span>;
  if (rec.ok) return <span className="inline-flex items-center gap-1 text-[11px] text-green-700"><CheckCircle2 className="w-3.5 h-3.5" />found</span>;
  if (rec.found?.length) {
    return (
      <span className="inline-flex items-start gap-1 text-[11px] text-red-700" title={rec.found.join('\n')}>
        <XCircle className="w-3.5 h-3.5 shrink-0 mt-px" />different value
      </span>
    );
  }
  return <span className={`inline-flex items-center gap-1 text-[11px] ${rec.required ? 'text-amber-700' : 'text-gray-500'}`}><Clock className="w-3.5 h-3.5" />not found yet</span>;
}

function DomainCard({ d, onCheck, onDelete, checking }) {
  const checked = !!d.records.some((r) => 'ok' in r);
  const st = STATUS[d.status] || STATUS.pending;
  // Doğrulanmış ve sorunsuz → yeşil kart, DNS tablosu kapalı (isteyen açar)
  const healthy = d.status === 'verified' && !d.problem;
  const [showRecords, setShowRecords] = useState(!healthy);
  useEffect(() => { setShowRecords(!healthy); }, [healthy]);
  const frame = d.problem ? 'border-red-300' : healthy ? 'border-green-300' : 'border-gray-200';
  return (
    <section className={`bg-white rounded-xl border ${frame} overflow-hidden`}>
      <header className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${showRecords ? 'border-b border-gray-100' : ''} ${healthy ? 'bg-green-50' : ''}`}>
        <div className="min-w-[260px] basis-0 grow flex items-center gap-3">
          {healthy && <CheckCircle2 className="w-7 h-7 text-green-600 shrink-0" />}
          {d.problem && <AlertTriangle className="w-7 h-7 text-red-600 shrink-0" />}
          <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">{d.domain}</h3>
            {healthy
              ? <span className="inline-flex items-center gap-1 rounded-full bg-green-600 px-2.5 py-0.5 text-[11px] font-semibold text-white"><Check className="w-3 h-3" />Verified</span>
              : <Badge tone={d.problem ? 'red' : st.tone}>{d.problem ? 'Needs attention' : st.label}</Badge>}
          </div>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {d.status === 'verified'
              ? <>Ready to send · e.g. <b className="text-gray-700">{d.fromExample}</b> · checked automatically every day</>
              : 'Add the records below at your DNS provider. Pending domains are re-checked every hour; you can also click "Check".'}
            {d.lastCheckedAt && <> · last checked {new Date(d.lastCheckedAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}</>}
          </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {healthy && (
            <button className="text-xs text-green-700 hover:text-green-900 mr-1" onClick={() => setShowRecords((v) => !v)}>
              {showRecords ? 'Hide DNS records' : 'Show DNS records'}
            </button>
          )}
          <button className={btnGhost} onClick={() => onCheck(d)} disabled={checking}>
            {checking ? <Loader2 className="animate-spin" /> : <RefreshCw />}Check
          </button>
          <button className={btnGhost} onClick={() => onDelete(d)} title="Remove domain"><Trash2 /></button>
        </div>
      </header>
      {d.problem && (
        <div className="mx-4 mt-3 flex gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />{d.problem}
        </div>
      )}
      {showRecords && <>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
              <th className="px-4 py-2 font-semibold w-[90px]">Purpose</th>
              <th className="px-2 py-2 font-semibold w-[60px]">Type</th>
              <th className="px-2 py-2 font-semibold">Name (host)</th>
              <th className="px-2 py-2 font-semibold">Value</th>
              <th className="px-4 py-2 font-semibold w-[130px]">Status</th>
            </tr>
          </thead>
          <tbody>
            {d.records.map((r) => (
              <tr key={r.key} className="border-b border-gray-50 last:border-0 align-top">
                <td className="px-4 py-2 text-xs text-gray-700">
                  {r.purpose}
                  {!r.required && <div className="text-[10px] text-gray-400">recommended</div>}
                </td>
                <td className="px-2 py-2 text-xs font-medium text-gray-700">{r.type}</td>
                <td className="px-2 py-2 max-w-[260px]"><CopyCell value={r.name} /></td>
                <td className="px-2 py-2 max-w-[320px]"><CopyCell value={r.value} /></td>
                <td className="px-4 py-2"><RecordState rec={r} checked={checked} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(d.inheritedDmarc || d.dkimStatus || d.mailFromStatus) && (
        <footer className="px-4 py-2.5 border-t border-gray-100 text-[11px] text-gray-500 space-y-0.5">
          {d.inheritedDmarc && <div>No DMARC record on this subdomain, but the parent domain has one (<code>{d.inheritedDmarc.name}</code>), which applies.</div>}
          {(d.dkimStatus || d.mailFromStatus) && <div>AWS SES: DKIM <b>{d.dkimStatus || '-'}</b> · MAIL FROM <b>{d.mailFromStatus || '-'}</b></div>}
        </footer>
      )}
      </>}
    </section>
  );
}

function DomainsTab({ brandId, onDomains }) {
  const [domains, setDomains] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [busy, setBusy] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try { setDomains((await mailApi(brandId, '/domains')).domains); }
    catch (e) { toast.error(`Could not load: ${e.message}`); setDomains([]); }
  }, [brandId]);
  useEffect(() => { setDomains(null); load(); }, [load]);
  useEffect(() => { if (domains) onDomains?.(domains); }, [domains]); // eslint-disable-line react-hooks/exhaustive-deps

  const replace = (d) => setDomains((list) => list.map((x) => (x.id === d.id ? d : x)));

  const check = async (d) => {
    setCheckingId(d.id);
    try {
      const { domain } = await mailApi(brandId, `/domains/${d.id}/check`, { method: 'POST' });
      replace(domain);
      if (domain.status === 'verified') toast.success(`${domain.domain} verified`);
    } catch (e) { toast.error(e.message); }
    finally { setCheckingId(null); }
  };

  const add = async () => {
    setBusy(true);
    try {
      const { domain } = await mailApi(brandId, '/domains', { method: 'POST', body: { domain: newDomain } });
      setDomains((list) => [...list, domain]);
      setAdding(false);
      setNewDomain('');
      toast.success(`${domain.domain} added to AWS SES — DNS records are ready`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await mailApi(brandId, `/domains/${toDelete.id}`, { method: 'DELETE' });
      setDomains((list) => list.filter((x) => x.id !== toDelete.id));
      setToDelete(null);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold text-gray-900">Sending domains</h2>
          <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">
            Emails are sent from the brand's own domain. Use a subdomain (e.g. <code>mail.brand.com</code>) so the root domain's
            existing mail and SPF settings stay untouched and sending reputation stays with the brand.
            Verified domains are checked automatically every day; you get an email if something breaks.
          </p>
        </div>
        <button className={btnPrimary} onClick={() => setAdding(true)}><Plus />Add domain</button>
      </div>

      {domains === null ? (
        <div className="flex items-center justify-center py-16 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading…</div>
      ) : domains.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 py-12 text-center">
          <Globe className="w-6 h-6 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-700 font-medium">No sending domains for this brand yet</p>
          <p className="text-xs text-gray-500 mt-1">Add a domain to see the DNS records you need to create.</p>
        </div>
      ) : (
        domains.map((d) => <DomainCard key={d.id} d={d} onCheck={check} onDelete={setToDelete} checking={checkingId === d.id} />)
      )}

      {adding && (
        <Modal
          title="Add sending domain"
          onClose={() => !busy && setAdding(false)}
          footer={<>
            <button className={btnGhost} onClick={() => setAdding(false)} disabled={busy}>Cancel</button>
            <button className={btnPrimary} onClick={add} disabled={busy || !newDomain.trim()}>{busy && <Loader2 className="animate-spin" />}Add</button>
          </>}
        >
          <label className="block text-xs font-medium text-gray-700 mb-1">Domain</label>
          <input
            className={inputCls}
            placeholder="mail.brand.com"
            value={newDomain}
            autoFocus
            onChange={(e) => setNewDomain(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && newDomain.trim() && !busy) add(); }}
          />
          <p className="text-[11px] text-gray-500 mt-2 leading-snug">
            This creates an AWS SES identity for the domain and lists the DNS records to add (3 DKIM, MX + SPF for MAIL FROM, DMARC).
            Nothing changes until the records are added to DNS.
          </p>
        </Modal>
      )}

      {toDelete && (
        <Modal
          title="Remove domain"
          onClose={() => !busy && setToDelete(null)}
          footer={<>
            <button className={btnGhost} onClick={() => setToDelete(null)} disabled={busy}>Cancel</button>
            <button className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-40" onClick={remove} disabled={busy}>
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}Remove
            </button>
          </>}
        >
          <p className="text-sm text-gray-700"><b>{toDelete.domain}</b> will be removed from this brand.</p>
          <p className="text-xs text-gray-500 mt-2">
            {toDelete.adopted
              ? 'This domain already existed in AWS SES. Its SES identity is kept; it is only removed from this list.'
              : 'The AWS SES identity is deleted too and sending from this domain stops. You can remove the DNS records separately.'}
          </p>
        </Modal>
      )}
    </div>
  );
}

export default function EmailPlatform() {
  const { selectedBrand } = useAuth();
  const [tab, setTab] = useState('campaigns');
  // Menüde Settings altında domain durumu (yeşil Verified) — sayfa açılınca bir kez çekilir, domain sekmesi güncelledikçe tazelenir
  const [domains, setDomains] = useState([]);
  const brandId = selectedBrand?.id;
  useEffect(() => {
    if (!brandId) return;
    setDomains([]);
    mailApi(brandId, '/domains').then((r) => setDomains(r.domains)).catch(() => {});
  }, [brandId]);

  if (!selectedBrand?.id) {
    return <div className="p-8 text-sm text-gray-500">Select a brand first.</div>;
  }

  const groups = [['Sending', SEND_TABS], ['Settings', SETTINGS_TABS]];

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-gray-50 rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-indigo-600" />
          <div>
            <h1 className="text-[15px] font-semibold text-gray-900">Glowy Mail</h1>
            <p className="text-xs text-gray-500">{selectedBrand.name} · campaign and automation emails via AWS SES</p>
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        <aside className="w-[220px] shrink-0 bg-white border-r border-gray-200 overflow-y-auto flex flex-col">
          {groups.map(([group, tabs]) => (
            <nav key={group} className="py-2 border-b border-gray-100">
              <h2 className="px-3 pt-1 pb-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{group}</h2>
              {tabs.map((t) => {
                const Icon = t.icon;
                const active = tab === t.key;
                if (t.soon) {
                  return (
                    <div key={t.key} className="w-full flex items-center gap-2 px-3 py-2 border-l-2 border-transparent text-xs text-gray-400 cursor-default" title="Not built yet">
                      <Icon className="w-3.5 h-3.5 shrink-0 text-gray-300" />{t.label}
                      <span className="ml-auto text-[10px] text-gray-400">soon</span>
                    </div>
                  );
                }
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`w-full flex items-center gap-2 px-3 py-2 border-l-2 text-left text-xs ${active ? 'border-indigo-600 bg-indigo-50/60 text-gray-900 font-medium' : 'border-transparent text-gray-600 hover:bg-gray-50'}`}
                  >
                    <Icon className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-indigo-600' : 'text-gray-400'}`} />{t.label}
                  </button>
                );
              })}
              {group === 'Settings' && domains.length > 0 && (
                <div className="px-3 pt-1.5 pb-1 space-y-1.5">
                  {domains.map((d) => {
                    const healthy = d.status === 'verified' && !d.problem;
                    const tone = healthy ? 'border-green-200 bg-green-50 text-green-800'
                      : d.problem ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800';
                    return (
                      <button key={d.id} type="button" onClick={() => setTab('domains')} className={`w-full text-left rounded-lg border px-2.5 py-1.5 ${tone}`}>
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold">
                          {healthy ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                            : d.problem ? <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0" /> : <Clock className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
                          {healthy ? 'Verified' : d.problem ? 'Needs attention' : 'Waiting for DNS'}
                        </div>
                        <div className="text-[10.5px] mt-0.5 truncate opacity-80" title={d.domain}>{d.domain}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </nav>
          ))}
          <div className="mt-auto p-3 border-t border-gray-100 text-[10px] text-gray-400 leading-snug flex gap-1.5">
            <AlertTriangle className="w-3 h-3 shrink-0 mt-px" />
            Sending requires an İYS-approved list and an unsubscribe link.
          </div>
        </aside>

        <main className="flex-1 min-w-0 p-6 overflow-y-auto">
          {tab === 'campaigns' && <CampaignsTab key={selectedBrand.id} brandId={selectedBrand.id} />}
          {tab === 'contacts' && <ContactsTab key={selectedBrand.id} brandId={selectedBrand.id} />}
          {tab === 'templates' && <TemplatesTab key={selectedBrand.id} brandId={selectedBrand.id} />}
          {tab === 'domains' && <DomainsTab brandId={selectedBrand.id} onDomains={setDomains} />}
        </main>
      </div>
    </div>
  );
}
